// WoodAlu.tsx
import React, { useEffect, useState } from "react";
import { getWoodAluImages } from "../../../services/image_services";
import LoadingImage from "../../../components/LoadingImage";
import ProductMain from "../../../components/ProductMain";
import Description from "../../../components/Description";
import colors from "../../../styles/colors";
import { motion } from "framer-motion";
import AccessoryContainer from "../../../components/AccessoryContainer";
import SectionTable from "../../../components/SectionTable";
import { useTranslation } from "react-i18next";
import SEO from "../../../utils/SEO";

const windowProfiles = [
  {
    type: "68",
    profileDimensions: "68 mm",
    visibleWidth: "N/A mm",
    thermalInsulation: "N/A W/m²K",
  },
  {
    type: "78",
    profileDimensions: "78 mm",
    visibleWidth: "N/A mm",
    thermalInsulation: "N/A W/m²K",
  },
  {
    type: "90",
    profileDimensions: "90 mm",
    visibleWidth: "N/A mm",
    thermalInsulation: "N/A W/m²K",
  },
  {
    type: "110",
    profileDimensions: "110 mm",
    visibleWidth: "N/A mm",
    thermalInsulation: "N/A W/m²K",
  },
];

const WoodAlu: React.FC = () => {
  const { t } = useTranslation();
  const [images, setImages] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(windowProfiles[0].type);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.style.overflowX = "hidden";
    return () => {
      document.body.style.overflowX = "auto";
    };
  }, []);

  useEffect(() => {
    const fetchImages = async () => {
      setIsLoading(true);

      try {
        const urls = await getWoodAluImages(); // Lokális képlekérdezés
        setImages(urls);
      } catch (error) {
        console.error("Error loading wood-alu images:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchImages();
  }, []);

  return (
    <div
      className="flex flex-col items-center font-serif justify-center min-h-screen overflow-hidden"
      style={{ backgroundColor: colors.pastelCream }}
    >
      {/* SEO meta tags */}
      <SEO
        title={t("seo_meta.wood_alu.metaTitle")}
        description={t("seo_meta.wood_alu.metaDescription")}
      />

      {isLoading ? (
        <div className="w-full h-screen flex items-center justify-center">
          <LoadingImage />
        </div>
      ) : (
        <>
          <ProductMain
            image="/assets/images/products/windows/wood_alu/wood_alu_main.webp"
            title={t("wood_alu.title")}
            description={t("wood_alu.subTitle")}
          />

          <Description
            image={images[0]}
            title={t("wood_alu.descTitle1")}
            text={t("wood_alu.desc1")}
            imageOnRight={false}
            isFirst={true}
          />

          <Description
            image={images[1]}
            title={t("wood_alu.descTitle2")}
            text={t("wood_alu.desc2")}
            imageOnRight={true}
            isLast={true}
          />

          {/* Details Section */}
          <div
            className="w-full max-h-auto min-h-[70vh] p-4 flex flex-col lg:flex-row items-center"
            style={{ backgroundColor: colors.darkBrown }}
          >
            {/* Image Container */}
            <div className="w-full sm:w-1/2 h-full flex object-contain justify-center items-center">
              <img
                src={images[2]}
                loading="lazy"
                alt="Wood-Aluminium Window"
                className="w-full h-auto lg:max-h-[100vh] object-contain"
              />
            </div>

            {/* Framer Motion Animációs Container */}
            <div
              className="w-full sm:w-1/2 h-full flex flex-col items-start p-8 space-y-4"
              style={{ color: colors.lightCream }}
            >
              <motion.h2
                initial={{ opacity: 0 }}
                whileInView={{
                  opacity: 1,
                  transition: { delay: 0.2, duration: 0.5 },
                }}
                viewport={{ once: false, amount: 0.5 }}
                className="lg:text-5xl text-xl md:text-4xl mt-4 mb-6"
              >
                {t("wood_alu.detailsTitle")}
              </motion.h2>

              <motion.ul className="space-y-4 text-lg md:text-xl lg:text-xl list-disc ml-6">
                <motion.li
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{
                    opacity: 1,
                    y: 0,
                    transition: { delay: 0.3, duration: 0.5 },
                  }}
                  viewport={{ once: false, amount: 0.5 }}
                >
                  {t("wood_alu.feature1.text")}
                </motion.li>

                <motion.li
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{
                    opacity: 1,
                    y: 0,
                    transition: { delay: 0.4, duration: 0.5 },
                  }}
                  viewport={{ once: false, amount: 0.5 }}
                >
                  {t("wood_alu.feature2.text")}
                </motion.li>

                <motion.li
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{
                    opacity: 1,
                    y: 0,
                    transition: { delay: 0.5, duration: 0.5 },
                  }}
                  viewport={{ once: false, amount: 0.5 }}
                >
                  {t("wood_alu.feature3.text")}
                </motion.li>

                <motion.li
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{
                    opacity: 1,
                    y: 0,
                    transition: { delay: 0.6, duration: 0.5 },
                  }}
                  viewport={{ once: false, amount: 0.5 }}
                >
                  {t("wood_alu.feature4.text")}
                </motion.li>

                <motion.li
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{
                    opacity: 1,
                    y: 0,
                    transition: { delay: 0.7, duration: 0.5 },
                  }}
                  viewport={{ once: false, amount: 0.5 }}
                >
                  {t("wood_alu.feature5.text")}
                </motion.li>

                <motion.li
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{
                    opacity: 1,
                    y: 0,
                    transition: { delay: 0.8, duration: 0.5 },
                  }}
                  viewport={{ once: false, amount: 0.5 }}
                >
                  {t("wood_alu.feature6.text")}
                </motion.li>
              </motion.ul>
            </div>
          </div>

          {/* Horizontal Divider */}
          <div
            className="w-full max-h-[20vh] pt-12 pb-12 flex justify-center"
            style={{ backgroundColor: colors.darkBrown }}
          >
            <hr
              className="w-[23%] sm:w-[15%] md:w-[15%] lg:w-[10%] h-0.5 border-t-0 opacity-100 dark:opacity-80"
              style={{ backgroundColor: colors.pastelCream }}
            />
          </div>

          <SectionTable
            title={t("wood_alu.tableTitle")}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            // sectionTableImages={sectionImages}
            windowProfiles={windowProfiles}
          />

          {/* Window Accessories Container */}
          <AccessoryContainer />
        </>
      )}
    </div>
  );
};

export default WoodAlu;
